import React from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { CalendarDaysIcon, ClockIcon } from '@heroicons/react/24/outline';
import NoImg from '../assets/images/no-image-icon-23485.png';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- Interfaces ---
interface Step {
  id: number;
  order: number;
  place: string;
  place_name: string;
  activity: string;
  start_time: string;
  duration_minutes: number;
  suggestion: string;
}

interface Plan {
  id: number;
  name: string;
  description: string;
  suggested_season: string;
  suggested_months: string;
  created_at: string;
  steps: Step[];
}

interface PlaceImage {
  image_id: string;
  file_url: string;
  filename: string;
}

interface PlaceDetail {
  place_id: string;
  name: string;
  images: PlaceImage[];
}

interface PlanCardProps {
  plan: Plan;
}

// --- Helper Functions ---
// Fix 1: unwrap the API envelope
const fetchPlaceDetails = async (placeId: string): Promise<PlaceDetail> => {
  const res = await fetch(`${BASE_URL}/api/places/${placeId}/`);
  if (!res.ok) throw new Error("Failed to fetch place");
  const json = await res.json();
  // API returns { success, data: PlaceDetail } or PlaceDetail directly
  return json.data ?? json;
};

// Fix 2: handle absolute URLs
const getImageUrl = (fileUrl: string | undefined) => {
  if (!fileUrl) return NoImg;
  if (fileUrl.startsWith('http')) return fileUrl;   // ← already absolute
  return `${BASE_URL}${fileUrl}`;
};

const formatTime = (timeString: string) => {
  return timeString.split(':').slice(0, 2).join(':');
};


// --- Main Component ---
const PlanCard: React.FC<PlanCardProps> = ({ plan }) => {
  // Prepare the queries for the first 4 steps
  const placeQueries = useQueries({
    queries: plan.steps.slice(0, 4).map((step) => ({
      queryKey: ['place', step.place],
      queryFn: () => fetchPlaceDetails(step.place),
      enabled: !!step.place,
      staleTime: 10 * 60 * 1000,
    })),
  });

  // Extract images safely from the query results
  const images = placeQueries.map((query) => {
    if (query.data?.images && query.data.images.length > 0) {
      return getImageUrl(query.data.images[0].file_url);
    }
    return NoImg;
  });

  return (
    <Link to={`/plans/${plan.id}`}>
      <div className="group border rounded-lg overflow-hidden hover:shadow-sm transition">
        {/* Image Section - دقیقاً مشابه PlaceCard */}
        <div className="relative w-full bg-gray-100">
          {plan.steps.length < 4 ? (
            <img
              src={images[0] || NoImg}
              alt={plan.name}
              className="w-full h-60 object-cover group-hover:brightness-75 transition"
              onError={(e) => (e.currentTarget.src = NoImg)}
            />
          ) : (
            <div className="grid grid-cols-2 grid-rows-2 w-full h-60">
              {[0, 1, 2, 3].map((index) => (
                <div key={index} className="relative overflow-hidden border border-white/20">
                  <img
                    src={images[index] || NoImg}
                    alt={`Destination ${index + 1}`}
                    className="w-full h-full object-cover group-hover:brightness-75 transition"
                    onError={(e) => (e.currentTarget.src = NoImg)}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-md text-xs font-bold text-gray-700 flex items-center gap-1 shadow-sm z-10">
            <CalendarDaysIcon className="w-3 h-3" />
            {plan.suggested_season}
          </div>
        </div>

        {/* Content Section - دقیقاً مشابه PlaceCard */}
        <div className="p-3 font-myYekanFaNumRegular">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-myYekanFaNumMedium">{plan.name}</h2>
            <div className="flex justify-end gap-1">
              {plan.steps.length > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  ({plan.steps.length} مقصد)
                </div>
              )}
            </div>
          </div>

          {/* Steps Preview - مشابه ساختار PlaceCard */}
          <div className="mt-2">
            {plan.steps.slice(0, 3).map((step) => (
              <div key={step.id} className="flex items-start gap-2 text-sm mt-1">
                <ClockIcon className="h-4 w-4 ml-1 mt-0.5 flex-shrink-0 text-gray-400" />
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 truncate">{step.place_name}</span>
                    <span className="text-xs text-gray-500 mr-2">
                      {formatTime(step.start_time)}
                    </span>
                  </div>
                  <div className="text-gray-400 text-xs truncate">{step.activity}</div>
                </div>
              </div>
            ))}
            {plan.steps.length > 3 && (
              <div className="text-center text-xs text-green-600 font-medium mt-1">
                +{plan.steps.length - 3} مقصد دیگر
              </div>
            )}
          </div>

          {plan.description && (
            <div className="text-xs mt-3">
              <p className="line-clamp-2 text-justify">
                {plan.description}
              </p>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default PlanCard;